import { Controller } from "@hotwired/stimulus";
import {
  computePosition,
  offset,
  flip,
  shift,
  autoUpdate,
} from "@floating-ui/dom";

// Tooltip positioned with Floating UI: opens on hover, keyboard focus or tap, and
// flips/shifts to stay on-screen (repositioning on scroll and resize). Hover and
// focus are transient; a tap pins it open — touch has no hover — until an outside
// tap or Escape.
//
//   <span data-controller="tooltip" class="relative ...">
//     <button data-tooltip-target="trigger"
//             data-action="mouseenter->tooltip#show mouseleave->tooltip#hide
//                          focus->tooltip#show blur->tooltip#hide click->tooltip#toggle">i</button>
//     <span data-tooltip-target="content" class="fixed opacity-0 transition-opacity ...">…</span>
//   </span>
export default class extends Controller {
  static targets = ["trigger", "content"];
  static values = { placement: { type: String, default: "top-end" } };

  connect() {
    this.onOutside = (event) => {
      if (!this.element.contains(event.target)) this.dismiss();
    };
    this.onKey = (event) => {
      if (event.key === "Escape") this.dismiss();
    };
  }

  show() {
    this.reveal();
  }

  hide() {
    if (this.pinned) return;
    this.conceal();
  }

  toggle(event) {
    event.preventDefault();
    if (this.pinned) {
      this.dismiss();
      return;
    }
    this.pinned = true;
    this.reveal();
    requestAnimationFrame(() => {
      document.addEventListener("click", this.onOutside);
      document.addEventListener("keydown", this.onKey);
    });
  }

  reveal() {
    if (this.visible) return;
    this.visible = true;
    this.stopAutoUpdate = autoUpdate(
      this.triggerTarget,
      this.contentTarget,
      () => this.position(),
    );
  }

  conceal() {
    if (!this.visible) return;
    this.visible = false;
    this.contentTarget.style.opacity = "";
    if (this.stopAutoUpdate) {
      this.stopAutoUpdate();
      this.stopAutoUpdate = null;
    }
  }

  dismiss() {
    this.pinned = false;
    this.conceal();
    document.removeEventListener("click", this.onOutside);
    document.removeEventListener("keydown", this.onKey);
  }

  position() {
    computePosition(this.triggerTarget, this.contentTarget, {
      placement: this.placementValue,
      strategy: "fixed",
      middleware: [offset(8), flip({ padding: 8 }), shift({ padding: 8 })],
    }).then(({ x, y }) => {
      // Only reveal once positioned, so it never flashes at the top-left corner.
      if (!this.visible) return;
      Object.assign(this.contentTarget.style, {
        left: `${x}px`,
        top: `${y}px`,
        opacity: "1",
      });
    });
  }

  disconnect() {
    this.dismiss();
  }
}
